import React, { useContext, useEffect, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import TextInput from '../TextInput';
import { CustomerContext } from '../../contexts/CustomerContext';
import styles from './AccessCodeOnlyForm.module.scss';
import useQueryString from '../../hooks/use-query-string';
import { routes } from '../../routes';
import { Student, StudentImage, School } from '../../models';
import AffirmationText from './AffirmationText';
import SchoolInput from '../SchoolInput';
import useSchools from '../../hooks/useSchools';
import { isValidEmailFormat, suggestEmail } from '../../utils/emailValidation';
import shoobMark from '../../assets/shoob-mark.png';

// Legacy export -- some other files import this. Kept as a thin wrapper
// around the shared isValidEmailFormat so we don't have two email regexes
// drifting apart.
export function isValidEmail(email) {
  return isValidEmailFormat(email);
}

// Access codes generated by StudentImage#user_friendly_code are exactly 6
// characters, but the customer's physical sitting card / proof / composite
// prints the code with a source-marker prefix ("S-", "P-", "C-", "M-").
// The fetch path already strips those prefixes; the validator mirrors that
// stripping so `P-H2K7QR` (8 chars raw / 6 chars parsed) counts as valid.
function stripAccessCodePrefix(input) {
  const upper = (input || '').toUpperCase();
  const match = upper.match(/^[SPCM]-(.+)$/);
  return match ? match[1] : upper;
}

function isValidAccessCode(input) {
  return stripAccessCodePrefix(input).length >= 6;
}

// Local, page-scoped strings dictionary. Adding proper app-wide i18n
// (react-i18next) is a separate project; this covers just the entry page
// via a `?lang=es` query param so Spanish-speaking parents can complete
// the code entry step. If i18n gets installed later, retire this dict.
const STRINGS = {
  en: {
    langToggleLabel: 'Español',
    titlePictures: 'Purchase Pictures',
    titleSenior: 'Purchase Senior Pictures',
    subtitle: 'Enter your student’s access code to get started.',
    notFoundTitle: 'We couldn’t find a match.',
    notFoundBody: 'Double-check your access code and school. If your photos were taken recently, they might not be ready to view yet.',
    notFoundLinkPrefix: 'Don’t have your code? ',
    notFoundLink: 'Look up your code →',
    schoolLabel: 'School',
    schoolPlaceholder: 'Type your school name',
    emailLabel: 'Email',
    emailPlaceholder: 'you@example.com',
    accessCodeLabel: 'Access code',
    accessCodePlaceholder: 'e.g. H2K7QR',
    whereIsCode: 'Where do I find my Access Code?',
    hideHelp: 'Hide help',
    helperText: (example) => (
      // Rules pulled from StudentImage#user_friendly_code (app/models/
      // student_image.rb): 6 uppercase alphanumeric chars, deliberately
      // excluding O / 0 / I / 1 / L to avoid printed-character confusion.
      // `translate="no"` keeps the product-specific card name and the
      // code example intact when a parent uses their browser's built-in
      // Translate feature.
      <>Your access code is printed on the <span translate="no">sitting card</span> or proof sheet sent home with students. It’s 6 characters — a mix of letters and numbers (for example <span translate="no">{example}</span>).</>
    ),
    required: '* Required',
    continue: 'Continue',
    noCodePrefix: 'Don’t have an access code? ',
    noCodeLink: 'Look up your code →',
    privacyTitle: 'Your Privacy Matters',
    privacyBody: 'Your photos are stored securely and only accessible with your unique code.',
    emailInvalid: 'That doesn’t look like a complete email address.',
    emailSuggestionPrefix: 'Did you mean ',
    emailSuggestionSuffix: '?',
    accessCodeInvalid: 'Access code needs to be at least 6 characters.',
  },
  es: {
    langToggleLabel: 'English',
    titlePictures: 'Comprar fotos',
    titleSenior: 'Comprar fotos de graduación',
    subtitle: 'Ingrese el código de acceso de su estudiante para comenzar.',
    notFoundTitle: 'No pudimos encontrar una coincidencia.',
    notFoundBody: 'Verifique su código de acceso y su escuela. Si sus fotos se tomaron recientemente, es posible que aún no estén listas para ver.',
    notFoundLinkPrefix: '¿No tiene su código? ',
    notFoundLink: 'Busque su código →',
    schoolLabel: 'Escuela',
    schoolPlaceholder: 'Escriba el nombre de su escuela',
    emailLabel: 'Correo electrónico',
    emailPlaceholder: 'usted@ejemplo.com',
    accessCodeLabel: 'Código de acceso',
    accessCodePlaceholder: 'ej. H2K7QR',
    whereIsCode: '¿Dónde encuentro mi código de acceso?',
    hideHelp: 'Ocultar ayuda',
    helperText: (example) => (
      <>Su código de acceso está impreso en la <span translate="no">sitting card</span> u hoja de pruebas enviada a casa con los estudiantes. Tiene 6 caracteres — una combinación de letras y números (por ejemplo <span translate="no">{example}</span>).</>
    ),
    required: '* Requerido',
    continue: 'Continuar',
    noCodePrefix: '¿No tiene un código de acceso? ',
    noCodeLink: 'Busque su código →',
    privacyTitle: 'Su privacidad es importante',
    privacyBody: 'Sus fotos se almacenan de forma segura y solo son accesibles con su código único.',
    emailInvalid: 'Eso no parece una dirección de correo electrónico completa.',
    emailSuggestionPrefix: '¿Quiso decir ',
    emailSuggestionSuffix: '?',
    accessCodeInvalid: 'El código de acceso debe tener al menos 6 caracteres.',
  },
};

export default function AccessCodeOnlyForm() {
  const [accessCode, setAccessCode] = useState('');
  const { customer, setCustomer } = useContext(CustomerContext);
  const { order, lang } = useQueryString();
  const location = useLocation();
  const t = STRINGS[lang === 'es' ? 'es' : 'en'];
  // Defined up here (not just before the form's return) because the
  // affirmation screen returns early and needs it too.
  const currentLang = lang === 'es' ? 'es' : 'en';
  const otherLang = lang === 'es' ? 'en' : 'es';
  // Preserve everything already in the URL when toggling language.
  const langToggleSearch = (() => {
    const params = new URLSearchParams(location.search);
    params.set('lang', otherLang);
    return `?${params.toString()}`;
  })();
  const [student, setStudent] = useState();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [packageId, setPackageId] = useState();
  const [studentImageSeason, setStudentImageSeason] = useState('');
  const history = useHistory();
  const schools = useSchools();
  const [email, setEmail] = useState(customer.email || '');
  const [emailError, setEmailError] = useState(false);
  const [showHelper, setShowHelper] = useState(false);
  // Same as the look-up page: on blur, flag a structurally-invalid email
  // and/or a "did you mean X?" suggestion for common typos. Both hints
  // sit under the email input; format-invalid wins if both apply.
  const [emailFormatBad, setEmailFormatBad] = useState(false);
  const [emailSuggestion, setEmailSuggestion] = useState(null);
  // Inline "code is too short" hint. Set on blur (so we don't yell mid-type)
  // and on submit; cleared as the user keeps typing.
  const [accessCodeInvalid, setAccessCodeInvalid] = useState(false);
  // Required-field validation stays hidden until the user tries to submit
  // with something missing -- then a red "Required" hint + red field
  // border appears next to each empty field, and clears as they fill it in.
  const [showRequired, setShowRequired] = useState(false);
  // Track school selection LOCALLY (null until handleSchoolChange fires).
  // customer.currentSchool comes from context and can be non-null from a
  // prior visit even when this form's SchoolInput is visibly empty; trusting
  // it as "the user picked a school on this page" let a stale value slip
  // through and the API fired without a real selection, producing the
  // "not found" banner instead of a "school required" hint.
  const [pickedSchool, setPickedSchool] = useState(null);
  // True while pickedSchool holds only the id+name stand-in taken from the
  // dropdown option, i.e. the pick landed before the school list resolved.
  const [needsFullSchool, setNeedsFullSchool] = useState(false);
  const missingAccessCode = showRequired && !accessCode;
  const missingSchool = showRequired && !pickedSchool;
  const missingEmail = showRequired && !email;

  const isYearbookOrder = order === 'yearbook_order';

  const fetchStudentData = (stuAccessCode, schoolId) => {
    let createdLocation;
    let parsedAccessCode;
    let isMultipleSeasonPackage;

    if (stuAccessCode.toUpperCase().includes('S-')) {
      parsedAccessCode = stuAccessCode.toUpperCase().split('S-')[1];
      createdLocation = 'sitting_cards';
    } else if (stuAccessCode.toUpperCase().includes('P-')) {
      parsedAccessCode = stuAccessCode.toUpperCase().split('P-')[1];
      createdLocation = 'proofs';
    } else if (stuAccessCode.toUpperCase().includes('M-')) {
      isMultipleSeasonPackage = true;
      parsedAccessCode = stuAccessCode.toUpperCase().split('M-')[1];
    } else {
      parsedAccessCode = stuAccessCode;
    }

    if (createdLocation) {
      customer.cart.createdLocation = createdLocation;
      customer.cart.save();
    }

    const studentScope = Student.selectExtra(['availableSeasonsToPurchase']);
    const selectExtras = ['packageId', isMultipleSeasonPackage && 'multiSeasonPackageId'].filter(n => n);

    StudentImage
      .selectExtra(selectExtras)
      .includes('student')
      .where({
        forAccessCodeAndSchool: [parsedAccessCode, schoolId],
        hasImagePresent: schoolId,
      })
      .merge({ student: studentScope })
      .all()
      .then((res) => {
        if (res.data.length === 0) return;

        const studentImage = res.data[0];

        if (isMultipleSeasonPackage && studentImage.multiSeasonPackageId) {
          setPackageId(studentImage.multiSeasonPackageId);
        } else {
          setPackageId(studentImage.packageId);
        }
        setStudent(studentImage.student);
        setStudentImageSeason(studentImage.folder);
        localStorage.setItem(`currentSeason-${studentImage.student.id}`, studentImage.folder);
      })
      .catch((error) => {
        console.error('Error fetching student data:', error);
      })
      .finally(() => {
        setHasSubmitted(true);
        setEmailError(false);
      });
  };

  // The option handed back by SchoolInput already carries the id and name --
  // everything this form needs to validate and run the lookup. Record it
  // immediately and only THEN try to upgrade to the full School record.
  //
  // Looking the option up in `schools` and storing the result directly used to
  // drop the selection outright: SchoolInput runs its own useSchools() fetch,
  // so its list can be populated (and accept a pick) while this component's is
  // still empty. find() returned undefined, pickedSchool stayed null, and the
  // form reported "School * Required" while react-select went on displaying
  // the chosen name from its own internal state.
  const handleSchoolChange = (opt) => {
    if (!opt) {
      setPickedSchool(null);
      setCustomer((c) => ({ ...c, currentSchool: null }));
      return;
    }
    // String() on both sides: ids arrive as strings from the JSON:API models
    // but as numbers from some callers, and === across the two silently misses.
    const full = schools.find((school) => String(school.id) === String(opt.value));
    const newSchool = full || { id: opt.value, name: opt.label };
    setNeedsFullSchool(!full);
    setPickedSchool(newSchool);
    setCustomer((c) => ({ ...c, currentSchool: newSchool }));
  };

  // Swap the id+name stand-in for the real School record once the list lands.
  // Downstream screens read accessCodeOnly / schoolTypeId off
  // customer.currentSchool, which the stand-in doesn't carry.
  //
  // Gated on the needsFullSchool flag rather than on comparing the found
  // record against the stored one. An identity comparison would rely on
  // useSchools handing back a referentially stable array; the moment it
  // doesn't, find() yields a new object every render, the comparison never
  // settles and the effect re-sets state forever (it hung the test suite
  // exactly once). The flag runs this once per pick either way.
  useEffect(() => {
    if (!needsFullSchool || !pickedSchool || schools.length === 0) return;
    const full = schools.find((school) => String(school.id) === String(pickedSchool.id));
    if (!full) return;
    setPickedSchool(full);
    setCustomer((c) => ({ ...c, currentSchool: full }));
    setNeedsFullSchool(false);
    // Deps are exhaustive (no disable comment needed): setCustomer is used in
    // its functional form, so this never closes over `customer`. Re-running
    // when pickedSchool's identity changes is harmless -- the flag is false by
    // then and the effect returns immediately.
  }, [schools, needsFullSchool, pickedSchool]);

  // Clear the "not-found" banner whenever ANY of the three inputs change --
  // otherwise a stale banner from a previous submit lingers while the user is
  // still fixing their entry. The banner is meant strictly for "you filled
  // everything in and we still couldn't find the student."
  useEffect(() => {
    setHasSubmitted(false);
  }, [accessCode, email, pickedSchool?.id]);

  // Force a clean-slate school selection on this page. Without this, a
  // school stored in CustomerContext from a previous visit would silently
  // apply on submit even though the SchoolInput visibly shows the
  // placeholder -- users would think they never picked a school.
  useEffect(() => {
    if (customer?.currentSchool) {
      setCustomer({ ...customer, currentSchool: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinue = (e) => {
    e.preventDefault();

    setCustomer({ ...customer, currentStudent: student });
    localStorage.setItem('isUsingAccessCode', 'true');

    if (isYearbookOrder) {
      history.push(routes.yearbooks({ sid: student.studentId, sch: customer.currentSchool.id }));
    } else if (!packageId) {
      history.push('/past_images');
    } else {
      history.push(routes.packageSelection({ pid: packageId, senior: studentImageSeason.toLowerCase().includes('senior') }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Flip the required-visibility flag before short-circuiting so ALL missing
    // fields light up at once (rather than making the user submit three times
    // to see each error). Also clear any stale "not-found" banner from a
    // previous submit -- the banner is only for the case where the user has
    // filled everything in and we still can't find them. Uses pickedSchool
    // (local) instead of customer.currentSchool so a context value carried
    // over from a prior visit can't slip through as "school selected."
    if (!accessCode || !pickedSchool || !email) {
      setShowRequired(true);
      setHasSubmitted(false);
      return;
    }
    setShowRequired(false);

    if (!isValidEmailFormat(email)) {
      setEmailError(true);
      setEmailFormatBad(true);
      return;
    }
    if (!isValidAccessCode(accessCode)) {
      setAccessCodeInvalid(true);
      return;
    }

    setCustomer({ ...customer, email });

    const studentScope = Student.selectExtra(['availableSeasonsToPurchase']);

    if (accessCode.toUpperCase().includes('C-')) {
      const parsedAccessCode = accessCode.toUpperCase().split('C-')[1];
      StudentImage
        .selectExtra(['packageId'])
        .includes('student')
        .where({ forAccessCodeAndSchool: [parsedAccessCode, pickedSchool.id] })
        .merge({ student: studentScope })
        .all()
        .then((res) => {
          if (res.data.length === 0) return;

          history.push(`/landing/composites?sid=${res.data[0].student.studentId}&sch=${pickedSchool.id}&access_code=${parsedAccessCode}`);
        })
        .finally(() => {
          setHasSubmitted(true);
          setEmailError(false);
        });
    } else {
      fetchStudentData(accessCode, pickedSchool.id);
    }
  };

  if (student && hasSubmitted) {
    return (
      <div className={styles.page} lang={currentLang}>
        <div className={styles.card}>
          <Link
            to={{ pathname: location.pathname, search: langToggleSearch }}
            className={styles.langToggle}
            translate="no"
            hrefLang={otherLang}
          >
            {t.langToggleLabel}
          </Link>
          <AffirmationText
            handleContinue={handleContinue}
            // Back to the entry form with school / email / code still filled
            // in, for the parent who reads the name and realises the code
            // they typed wasn't their student's.
            handleBack={() => {
              setStudent(undefined);
              setHasSubmitted(false);
            }}
            student={student}
            lang={currentLang}
          />
        </div>
      </div>
    );
  }

  const title = order === 'senior' ? t.titleSenior : t.titlePictures;

  return (
    // `lang` on the subtree tells Chrome/Safari/Edge what language the
    // content is in, which drives their "Translate this page?" offer and
    // improves translation quality vs. relying on auto-detection.
    <div className={styles.page} lang={currentLang}>
      <div className={styles.card}>
        <Link
          to={{ pathname: location.pathname, search: langToggleSearch }}
          className={styles.langToggle}
          // The toggle label is written in the OTHER language on purpose
          // (so an English viewer sees "Español" and vice-versa); marking
          // it translate="no" keeps the browser from re-translating it back.
          translate="no"
          hrefLang={otherLang}
        >
          {t.langToggleLabel}
        </Link>
        {/* Decorative: the brand mark repeats what the site header already
            shows, so it is hidden from screen readers rather than announced
            a second time before the page title. */}
        <div className={styles.logoBadge} aria-hidden="true">
          <img src={shoobMark} alt="" className={styles.logoMark} />
        </div>
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.subtitle}>{t.subtitle}</p>

        {hasSubmitted && !student && (
          <div className={styles.errorBanner} role="alert">
            <span className={styles.errorIcon} aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </span>
            <div className={styles.errorText}>
              <div className={styles.errorTitle}>{t.notFoundTitle}</div>
              <div className={styles.errorBody}>{t.notFoundBody}</div>
              <div className={styles.errorAction}>
                {t.notFoundLinkPrefix}
                {/* Carry the language across: without it a parent reading this
                    page in Spanish landed on an English lookup form. */}
                <Link to={routes.getAccessCode({ lang: currentLang })}>{t.notFoundLink}</Link>
              </div>
            </div>
          </div>
        )}

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className={`${styles.field} ${missingSchool ? styles.errorField : ''}`}>
            <label htmlFor="school-input">
              {t.schoolLabel}
              {missingSchool && (
                <span className={styles.required} aria-hidden="true">{t.required}</span>
              )}
            </label>
            <SchoolInput
              onChange={handleSchoolChange}
              hideLabel
              noDefault
              openOnTypeOnly
              placeholder={t.schoolPlaceholder}
              error={missingSchool}
            />
          </div>

          <div className={`${styles.field} ${missingEmail ? styles.errorField : ''}`}>
            <label htmlFor="email">
              {t.emailLabel}
              {missingEmail && (
                <span className={styles.required} aria-hidden="true">{t.required}</span>
              )}
            </label>
            <div className={styles.inputWithIcon}>
              <span className={styles.leadingIcon} aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </span>
              <TextInput
                id="email"
                val={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError(false);
                  if (emailFormatBad) setEmailFormatBad(false);
                  if (emailSuggestion) setEmailSuggestion(null);
                }}
                onBlur={() => {
                  if (!email) return;
                  setEmailFormatBad(!isValidEmailFormat(email));
                  setEmailSuggestion(suggestEmail(email));
                }}
                type="email"
                autoComplete="email"
                placeholder={t.emailPlaceholder}
                aria-required="true"
                aria-invalid={(missingEmail || emailError || emailFormatBad) || undefined}
              />
            </div>
            {emailFormatBad || (emailError && !missingEmail) ? (
              <div className={styles.errorHint}>{t.emailInvalid}</div>
            ) : emailSuggestion ? (
              <div className={styles.emailSuggestion}>
                {t.emailSuggestionPrefix}
                <button
                  type="button"
                  className={styles.emailSuggestionAction}
                  onClick={() => { setEmail(emailSuggestion); setEmailSuggestion(null); }}
                >
                  {emailSuggestion}
                </button>
                {t.emailSuggestionSuffix}
              </div>
            ) : null}
          </div>

          <div className={`${styles.field} ${(missingAccessCode || accessCodeInvalid) ? styles.errorField : ''}`}>
            <div className={styles.labelRow}>
              <label htmlFor="access-code">
                {t.accessCodeLabel}
                {missingAccessCode && (
                  <span className={styles.required} aria-hidden="true">{t.required}</span>
                )}
              </label>
              <button
                type="button"
                className={styles.helperToggle}
                onClick={() => setShowHelper((v) => !v)}
                aria-expanded={showHelper}
              >
                <span className={styles.helperIcon} aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </span>
                {showHelper ? t.hideHelp : t.whereIsCode}
              </button>
            </div>
            <div className={styles.inputWithIcon}>
              <span className={styles.leadingIcon} aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="11" width="16" height="10" rx="2" />
                  <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                </svg>
              </span>
              <TextInput
                id="access-code"
                val={accessCode}
                onChange={(e) => {
                  setAccessCode(e.target.value);
                  if (accessCodeInvalid) setAccessCodeInvalid(false);
                }}
                onBlur={() => {
                  if (!accessCode) return;
                  setAccessCodeInvalid(!isValidAccessCode(accessCode));
                }}
                placeholder={t.accessCodePlaceholder}
                autoComplete="off"
                autoCapitalize="characters"
                aria-required="true"
                aria-invalid={(missingAccessCode || accessCodeInvalid) || undefined}
              />
            </div>
            {accessCodeInvalid && !missingAccessCode && (
              <div className={styles.errorHint}>{t.accessCodeInvalid}</div>
            )}
            {showHelper && (
              <div className={styles.helperPanel}>
                {t.helperText(<code>H2K7QR</code>)}
              </div>
            )}
          </div>

          <button type="submit" className={styles.cta}>
            {t.continue}
          </button>
        </form>

        <p className={styles.secondaryRow}>
          {t.noCodePrefix}
          <Link to={routes.getAccessCode({ lang: currentLang })}>{t.noCodeLink}</Link>
        </p>
      </div>

      <div className={styles.privacyCallout}>
        <div className={styles.privacyTitle}>{t.privacyTitle}</div>
        <div className={styles.privacyBody}>{t.privacyBody}</div>
      </div>
    </div>
  );
}
